import { InlineLoading } from "../components/ContentLoading";
import { type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, RefreshCw } from "lucide-react";
import type { CustomerDocumentInput } from "../../shared/customer-documents";
import { Button } from "../components/ui/button";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { getOpsDocsBootstrap } from "./docs-api";
import { uploadCustomerDocumentFile } from "./customer-documents-api";

export type DocumentDraftProps = {
  value: CustomerDocumentInput;
  onChange: Dispatch<SetStateAction<CustomerDocumentInput>>;
};
function PageSource({
  value,
  onChange,
}: {
  value: CustomerDocumentInput;
  onChange: Dispatch<SetStateAction<CustomerDocumentInput>>;
}) {
  const pages = useQuery({ queryKey: ["ops", "docs", "bootstrap"], queryFn: getOpsDocsBootstrap });
  const options = pages.data?.pages.filter((page) => page.accessLevel === "manage") ?? [];
  const source = value.source;
  function choose(id: string) {
    const page = options.find((item) => item.id === id);
    if (page)
      onChange({
        ...value,
        title: value.title || page.title,
        source: { type: "page", pageId: page.id, revision: page.revision },
      });
  }
  return (
    <Field>
      <FieldLabel htmlFor="library-page">Internal page</FieldLabel>
      <div className="flex gap-2">
        <select
          id="library-page"
          className="customer-native-select flex-1"
          value={source.type === "page" ? source.pageId : ""}
          onChange={(event) => {
            choose(event.target.value);
          }}
          required
        >
          <option value="">Choose a page you manage</option>
          {options.map((page) => (
            <option key={page.id} value={page.id}>
              {page.title}
            </option>
          ))}
        </select>
        <Button
          type="button"
          variant="ghost"
          aria-label="Refresh internal pages"
          onClick={() => {
            void pages.refetch();
          }}
        >
          <RefreshCw />
        </Button>
      </div>
      {pages.isPending ? <InlineLoading label="Loading pages…" /> : null}
      {pages.error ? <FieldError>{pages.error.message}</FieldError> : null}
      <p className="text-sm text-muted-foreground">
        A copy of this version is shared. Later edits stay internal until you publish an update.
      </p>
      {source.type === "page" &&
      options.some((page) => page.id === source.pageId && page.revision !== source.revision) ? (
        <Button
          className="w-fit"
          variant="outline"
          onClick={() => {
            choose(source.pageId);
          }}
          type="button"
        >
          <RefreshCw /> Use latest page version
        </Button>
      ) : null}
    </Field>
  );
}
function PdfSource({
  slug,
  value,
  onChange,
  onUploadBusy,
}: { slug: string; onUploadBusy: (busy: boolean) => void } & DocumentDraftProps) {
  const upload = useMutation({
    mutationFn: (file: File) => uploadCustomerDocumentFile(slug, value.kind, file),
    onMutate: () => {
      onUploadBusy(true);
    },
    onSettled: () => {
      onUploadBusy(false);
    },
    onSuccess: (file) => {
      onChange((current) => ({
        ...current,
        title: current.title || file.filename.replace(/\.pdf$/i, ""),
        source: { type: "file", fileId: file.fileId },
      }));
    },
  });
  return (
    <Field>
      <FieldLabel htmlFor="library-pdf">PDF document</FieldLabel>
      {value.source.type === "file" && value.source.fileId ? (
        <p className="flex items-center gap-2 text-sm">
          <FileText className="size-4 text-link" /> {upload.data?.filename ?? "PDF attached"}
        </p>
      ) : null}
      <Input
        id="library-pdf"
        type="file"
        accept="application/pdf,.pdf"
        disabled={upload.isPending}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
      <p className="text-sm text-muted-foreground" role="status">
        {upload.isPending
          ? "Uploading PDF…"
          : "PDF, up to 10 MiB. The file stays private until published."}
      </p>
      {upload.error ? <FieldError>{upload.error.message}</FieldError> : null}
    </Field>
  );
}
export function CustomerDocumentSource({
  slug,
  value,
  onChange,
  canUsePages,
  onUploadBusy,
}: {
  slug: string;
  canUsePages: boolean;
  onUploadBusy: (busy: boolean) => void;
} & DocumentDraftProps) {
  function changeType(type: string) {
    if (type === "link") onChange({ ...value, source: { type: "link", url: "" } });
    if (type === "page") onChange({ ...value, source: { type: "page", pageId: "", revision: 1 } });
    if (type === "file") onChange({ ...value, source: { type: "file", fileId: "" } });
  }
  return (
    <section className="space-y-4 border-t pt-5" aria-label="Document source">
      <Field>
        <FieldLabel htmlFor="library-source">Document source</FieldLabel>
        <select
          className="customer-native-select"
          id="library-source"
          value={value.source.type}
          onChange={(event) => {
            changeType(event.target.value);
          }}
        >
          {canUsePages ? <option value="page">Internal Docs page</option> : null}
          <option value="file">Upload a PDF</option>
          <option value="link">Link to a document</option>
        </select>
      </Field>
      {value.source.type === "page" ? <PageSource value={value} onChange={onChange} /> : null}
      {value.source.type === "file" ? (
        <PdfSource slug={slug} value={value} onChange={onChange} onUploadBusy={onUploadBusy} />
      ) : null}
      {value.source.type === "link" ? (
        <Field>
          <FieldLabel htmlFor="library-url">Document link</FieldLabel>
          <Input
            id="library-url"
            type="url"
            placeholder="https://…"
            required
            value={value.source.url}
            onChange={(event) => {
              onChange({ ...value, source: { type: "link", url: event.target.value } });
            }}
          />
          <p className="text-sm text-muted-foreground">
            Use a link your customer can open in its document provider.
          </p>
        </Field>
      ) : null}
    </section>
  );
}
